import usePermissions from "@/hooks/use-permissions.hook.ts";
import { RecognitionEditModal } from "@/pages/settings/recognitions/widgets/RecognitionEditModal.tsx";
import { RecognitionGiftModal } from "@/pages/settings/recognitions/widgets/RecognitionGiftModal.tsx";
import { useRecognitionDelete } from "@/queries/use-recognitions.query.ts";
import { ApiRecognition } from "@/types/recognitions/recognition.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FontAwesomeIcon,
  useModals,
} from "@skailer-com/uikit";
import React from "react";
import { useTranslation } from "react-i18next";

export interface RecognitionActionsMenuProps {
  recognition: ApiRecognition;
  onBeforeAction?: () => void;
  asButton?: boolean;
}

export function RecognitionActionsMenu({
  recognition,
  onBeforeAction,
  asButton = false,
}: RecognitionActionsMenuProps) {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();
  const recognitionDelete = useRecognitionDelete();

  const canGift = canIEdit(ApiPermission.recognitions_mass_gift);

  const trigger = asButton ? (
    <Button variant={"secondary"} rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}>
      {t("recognition:actions")}
    </Button>
  ) : undefined;

  const openDeleteConfirmation = () => {
    openModal(ConfirmationModal, {
      question: t("recognition:recognition_delete_named", { title: recognition.title }),
      text: t("recognition:recognition_delete_confirm", { title: recognition.title }),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        await recognitionDelete.mutateAsync(recognition.id);
      },
    });
  };

  return (
    <DropdownMenu trigger={trigger}>
      <DropdownMenuItem
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          onBeforeAction?.();
          openModal(RecognitionEditModal, { recognition });
        }}
        title={t("common:edit")}
      />
      {canGift && recognition.can_gift && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              onBeforeAction?.();
              openModal(RecognitionGiftModal, { recognition });
            }}
            title={t("recognition:gift_recognition_title")}
            description={t("recognition:gift_recognition_caption")}
          />
        </>
      )}
      {!recognition.is_system && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              if (recognition.granted_count > 0) return;
              e.preventDefault();
              onBeforeAction?.();
              openDeleteConfirmation();
            }}
            disabled={recognition.granted_count > 0}
            className={recognition.granted_count > 0 ? "pointer-events-none opacity-70" : undefined}
            title={
              recognition.granted_count > 0
                ? t("recognition:recognition_delete_unavailable")
                : t("recognition:recognition_delete")
            }
            description={
              recognition.granted_count > 0
                ? t("recognition:recognition_delete_granted")
                : undefined
            }
          />
        </>
      )}
    </DropdownMenu>
  );
}
